/**
 * The stores desk: what the register says when somebody opens it, and what it
 * can answer without a model.
 *
 * `stores.ts` is the arithmetic. This is the reading of it — the tiles, the
 * summary somebody sees before the full desk, the material Sky is given, and
 * the questions answerable from the register itself. The split is the same one
 * the rest of the platform keeps: a number is computed in one place and
 * explained in another, so an explanation can never quietly invent a figure.
 *
 * Every tile here carries `assumed: true` while the register is entered rather
 * than read from a stores system, because that is what it is, and a screen
 * that shows typed figures in the same clothes as measured ones has lied by
 * layout rather than by words.
 */
import { t, tn } from "../i18n";
import type { SourceRef } from "./decision";
import {
  arrivesInTime,
  categoriesOf,
  categoryLabel,
  countAccuracy,
  daysUntil,
  fillRate,
  NO_SUPPLIER,
  onOrder,
  partsFor,
  reorderRun,
  rows,
  STATE_LABEL,
  summarise,
  type ReorderLine,
  type StockRow,
  type StoresRegister,
  type StoresSummary,
} from "./stores";

export interface StoresInputs {
  now: Date;
  siteCode: string;
  register: StoresRegister;
}

export interface StoresDesk {
  generatedAt: string;
  rows: StockRow[];
  summary: StoresSummary;
  fill: ReturnType<typeof fillRate>;
  accuracy: ReturnType<typeof countAccuracy>;
  byCategory: { key: string; label: string; lines: number; out: number; value: number | null }[];
  byLocation: { code: string; name: string; lines: number; value: number | null }[];
  run: ReturnType<typeof reorderRun>;
  /** lines that must be bought AND whose lead time is longer than the cover left */
  late: ReorderLine[];
  /** how many of them nobody has asked for yet — what a replenishment run would raise */
  toRequest: number;
  /** jobs the stores are holding up, worst first */
  waiting: { workOrder: string; short: number; blocked: boolean; readyAt: string | null; sentence: string }[];
  /** purchase orders promised before today and not delivered */
  overdue: { number: string; supplier: string; expectedAt?: string; daysLate: number | null; lines: number }[];
  tiles: {
    key: string;
    label: string;
    value: number | null;
    unit: string;
    text: string;
    tone?: "good" | "bad" | "warn";
    assumed?: boolean;
    sources: SourceRef[];
  }[];
  boardLine: string[];
  gaps: string[];
  figures: Set<string>;
  enteredBy?: string;
  enteredAt?: string;
}

export function buildStoresDesk(inp: StoresInputs): StoresDesk {
  const { now, register } = inp;
  const list = rows(register, now);
  const summary = summarise(register, now);
  const fill = fillRate(register, now);
  const accuracy = countAccuracy(register, now);
  const run = reorderRun(register, now);
  const late = run.flatMap((g) => g.lines).filter((l) => arrivesInTime(l).inTime === false);

  const byCategory = categoriesOf(register).map((c) => {
    const inCat = list.filter((r) => r.item.category === c.key);
    const values = inCat.map((r) => r.value);
    const known = values.filter((v): v is number => v !== null);
    return {
      key: c.key,
      label: c.label,
      lines: inCat.length,
      out: inCat.filter((r) => r.state === "out" || r.state === "expired").length,
      value: known.length === values.length && known.length > 0 ? known.reduce((a, b) => a + b, 0) : null,
    };
  }).filter((c) => c.lines > 0);

  const byLocation = register.locations
    .map((loc) => {
      const here = list.filter((r) => r.item.bins.some((b) => b.location === loc.code));
      let value: number | null = 0;
      for (const r of here) {
        const held = r.item.bins.filter((b) => b.location === loc.code).reduce((n, b) => n + b.onHand, 0);
        if (r.item.unitCost === undefined) {
          value = null;
          break;
        }
        value = (value ?? 0) + held * r.item.unitCost;
      }
      return { code: loc.code, name: loc.name, lines: here.length, value };
    })
    .filter((l) => l.lines > 0);

  /*
   * The jobs the stores are holding up.
   *
   * This is the list the maintenance planner wants and the one a stores system
   * almost never produces, because it reads the register from the shelf's side
   * rather than from the job's. Blocked first — a job with nothing on order is
   * not waiting, it is stopped.
   */
  const waiting = [...new Set(summary.waitingWorkOrders)]
    .map((wo) => {
      const parts = partsFor(wo, register, now);
      return {
        workOrder: wo,
        short: parts.short,
        blocked: parts.blocked,
        readyAt: parts.readyAt,
        sentence: sentenceFor(parts.short, parts.blocked, parts.readyAt, now),
      };
    })
    .filter((w) => w.short > 0)
    .sort((a, b) => Number(b.blocked) - Number(a.blocked) || b.short - a.short || a.workOrder.localeCompare(b.workOrder));

  const overdue = register.orders
    .filter((o) => (o.status === "open" || o.status === "part_received") && (daysUntil(o.expectedAt, now) ?? 1) < 0)
    .map((o) => ({
      number: o.number,
      supplier: o.supplier,
      expectedAt: o.expectedAt,
      daysLate: o.expectedAt ? -(daysUntil(o.expectedAt, now) ?? 0) : null,
      lines: o.lines.filter((l) => l.received < l.qty).length,
    }))
    .sort((a, b) => (b.daysLate ?? 0) - (a.daysLate ?? 0));

  const entered: SourceRef[] = [
    {
      kind: "entered",
      ref: "stores register",
      label: t("the stores register"),
      enteredBy: register.enteredBy,
    },
  ];

  const criticalShort = list.filter((r) => r.item.critical && (r.state === "out" || r.state === "expired"));

  const tiles: StoresDesk["tiles"] = [
    {
      key: "lines",
      label: t("lines in the catalog"),
      value: summary.lines,
      unit: "",
      text: t("across {n}", { n: tn(summary.categories, "{n} category", "{n} categories") }),
      tone: "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "value",
      label: t("on the shelf"),
      value: summary.value,
      unit: register.currency,
      text:
        summary.valueUnknown > 0
          ? t("{n} carry no unit cost and are left out of the total", { n: summary.valueUnknown })
          : t("valued at the last cost recorded for each line"),
      tone: "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "out",
      label: t("out of stock"),
      value: summary.out,
      unit: "",
      text:
        criticalShort.length > 0
          ? t("{n} of them critical spares", { n: criticalShort.length })
          : summary.out > 0
            ? t("none of them a critical spare")
            : t("every line has something available"),
      tone: criticalShort.length > 0 ? "bad" : summary.out > 0 ? "warn" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "reorder",
      label: t("below the reorder point"),
      value: summary.belowMin,
      unit: "",
      text:
        late.length > 0
          ? t("{n} cannot arrive before the shelf empties", { n: late.length })
          : t("each can be replenished within its lead time"),
      tone: late.length > 0 ? "bad" : summary.belowMin > 0 ? "warn" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "waiting",
      label: t("jobs waiting on a part"),
      value: waiting.length,
      unit: "",
      text:
        waiting.filter((w) => w.blocked).length > 0
          ? t("{n} with nothing on order", { n: waiting.filter((w) => w.blocked).length })
          : waiting.length > 0
            ? t("all of them have a delivery date")
            : t("no job is short of a part"),
      tone: waiting.some((w) => w.blocked) ? "bad" : waiting.length > 0 ? "warn" : "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "cover",
      label: t("days of cover, median"),
      value: summary.medianCover,
      unit: t("days"),
      text:
        summary.medianCover === null
          ? t("nothing has been drawn, so no rate can be computed")
          : tn(
              summary.coverUnknown,
              "{n} line has no consumption and is left out",
              "{n} lines have no consumption and are left out",
            ),
      tone: "good",
      assumed: true,
      sources: entered,
    },
    {
      key: "fill",
      label: t("issues filled from stock"),
      value: fill.pct,
      unit: "%",
      text:
        fill.pct === null
          ? t("nothing has been asked of the stores in the window")
          : t("{filled} of {asked} reservations filled in full", { filled: fill.filled, asked: fill.asked }),
      tone: fill.pct === null ? undefined : fill.pct >= 95 ? "good" : "warn",
      assumed: true,
      sources: entered,
    },
    {
      key: "accuracy",
      label: t("stock count accuracy"),
      value: accuracy.pct,
      unit: "%",
      text:
        accuracy.pct === null
          ? t("nothing has been counted in the last year")
          : t("{counted} lines counted, {uncounted} never counted", {
              counted: accuracy.counted,
              uncounted: accuracy.uncounted,
            }),
      tone: accuracy.pct === null ? undefined : accuracy.pct >= 98 ? "good" : "warn",
      assumed: true,
      sources: entered,
    },
  ];

  const boardLine: string[] = [
    t("{site} holds {lines} in stores, {value} on the shelf.", {
      site: inp.siteCode,
      lines: tn(summary.lines, "{n} line", "{n} lines"),
      value:
        summary.value === null
          ? t("nothing valued")
          : `${Math.round(summary.value).toLocaleString()} ${register.currency}`,
    }),
  ];
  if (summary.out > 0 || summary.belowMin > 0 || summary.expired > 0) {
    boardLine.push(
      t("{out} out of stock, {below} below the reorder point, {expired} past their shelf life.", {
        out: summary.out,
        below: summary.belowMin,
        expired: summary.expired,
      }),
    );
  }
  if (waiting.length > 0) {
    const blocked = waiting.filter((w) => w.blocked).length;
    boardLine.push(
      blocked > 0
        ? t("{n} waiting on a part, and {blocked} of those have nothing on order.", {
            n: tn(waiting.length, "{n} job is", "{n} jobs are"),
            blocked,
          })
        : t("{n} waiting on a part, all with a delivery date.", {
            n: tn(waiting.length, "{n} job is", "{n} jobs are"),
          }),
    );
  }
  if (criticalShort.length > 0) {
    boardLine.push(
      t("Critical spares not held: {list}.", { list: criticalShort.map((r) => r.item.name).join(", ") }),
    );
  }
  if (overdue.length > 0) {
    boardLine.push(
      t("{n} promised before today and not delivered.", {
        n: tn(overdue.length, "{n} purchase order was", "{n} purchase orders were"),
      }),
    );
  }

  const gaps: string[] = [
    t(
      "This register is entered by hand, not read from a stores system. No line is watched, and no reorder raises an alert on its own",
    ),
    t(
      "Usage is counted from movements booked here. Parts taken without being booked out are missing from every figure",
    ),
    t("Unit costs are the last price paid, not a valuation"),
  ];
  if (accuracy.uncounted > 0) {
    gaps.push(
      t("{n} have never been counted, so the accuracy above is measured over the rest", {
        n: tn(accuracy.uncounted, "{n} line", "{n} lines"),
      }),
    );
  }
  if (summary.coverUnknown > 0) {
    gaps.push(
      t("{n} have never been drawn, so they have no consumption rate and their cover is unknown rather than long", {
        n: tn(summary.coverUnknown, "{n} line", "{n} lines"),
      }),
    );
  }

  const material = materialForStores({ tiles, rows: list, boardLine, waiting, run });
  const figures = new Set<string>();
  for (const n of material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []) {
    figures.add(n);
    figures.add(n.replace(/,/g, ""));
  }
  for (let n = 0; n <= 12; n++) figures.add(String(n));

  return {
    generatedAt: now.toISOString(),
    rows: list,
    summary,
    fill,
    accuracy,
    byCategory,
    byLocation,
    run,
    late,
    toRequest: run.flatMap((g) => g.lines).filter((l) => !l.requested).length,
    waiting,
    overdue,
    tiles,
    boardLine,
    gaps,
    figures,
    enteredBy: register.enteredBy,
    enteredAt: register.enteredAt,
  };
}

function sentenceFor(short: number, blocked: boolean, readyAt: string | null, now: Date): string {
  if (blocked) return t("nothing on order");
  const days = daysUntil(readyAt ?? undefined, now);
  if (days === null) return tn(short, "{n} part short", "{n} parts short");
  if (days < 0) return tn(Math.abs(days), "the delivery is {n} day late", "the delivery is {n} days late");
  if (days === 0) return t("due today");
  return tn(days, "due in {n} day", "due in {n} days");
}

export const STORES_MODEL_PROMPT = [
  "You are advising an airport on its stores and purchasing from the material given and nothing else.",
  "You may explain and prioritise; you must not state any number that is not in the material.",
  "Every figure here was entered by a person, not read from a stores system. Say so when you use one.",
  "On hand, reserved and available are three different numbers. Never substitute one for another.",
  "Stock on order is not stock. Never count it as held.",
  "Never tell anybody a job can start. Whether a job can start is the maintenance planner's decision.",
  "Be brief and direct.",
].join(" ");

export function materialForStores(
  d: Pick<StoresDesk, "tiles" | "rows" | "boardLine" | "waiting" | "run">,
): string {
  const L: string[] = [];
  for (const tile of d.tiles) L.push(`Tile ${tile.label}: ${tile.value ?? "n/a"} — ${tile.text} [entered]`);
  for (const row of d.rows) {
    const i = row.item;
    L.push(
      `${i.sku} ${i.name} (${categoryLabel(i.category)}${i.critical ? ", critical spare" : ""}): ` +
        `${STATE_LABEL[row.state]}; on hand ${row.onHand} ${i.uom}, reserved ${row.reserved}, available ${row.available}; ` +
        `reorder point ${i.min}, maximum ${i.max}; on order ${row.onOrder}` +
        (row.expectedAt ? ` expected ${row.expectedAt}` : "") +
        `; used ${row.consumed365} in the year; cover ${row.cover === null ? "unknown — never drawn" : `${row.cover} days`}; ` +
        `lead time ${i.leadDays === undefined ? "not recorded" : `${i.leadDays} days`}; ` +
        `supplier ${i.supplier ?? "not recorded"}` +
        (row.waiting.length ? `; jobs waiting: ${row.waiting.join(", ")}` : "") +
        (i.critical ? `; critical because ${i.critical.why}` : ""),
    );
  }
  for (const w of d.waiting) L.push(`Job ${w.workOrder}: ${w.short} part(s) short — ${w.sentence}`);
  for (const g of d.run) {
    L.push(
      `To buy from ${g.supplier}: ${g.lines.map((l) => `${l.qty} × ${l.item.sku}`).join(", ")}` +
        (g.total === null ? " (total unknown — a line has no cost)" : ` (about ${Math.round(g.total)})`),
    );
  }
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

/**
 * What the register can answer by itself.
 *
 * Every branch either answers from the rows or says plainly that there is
 * nothing to report. None of them ever says a job may start: that decision
 * needs the job, the crew and the permit, none of which are on this desk.
 */
export function answerStores(q: string, d: StoresDesk): string | null {
  const text = q.toLowerCase();
  const line = (r: StockRow) =>
    `${r.item.sku} ${r.item.name} — ${STATE_LABEL[r.state]}, ${r.available} ${r.item.uom} available of ${r.onHand} on hand`;

  if (/out of stock|stockout|nothing left|empty/.test(text)) {
    const bad = d.rows.filter((r) => r.state === "out" || r.state === "expired");
    return bad.length
      ? bad.map(line).join("\n")
      : t("Every line in the catalog has something available.");
  }
  if (/critical|spare/.test(text)) {
    const crit = d.rows.filter((r) => r.item.critical);
    if (!crit.length) return t("No line is marked as a critical spare.");
    return crit
      .map((r) => `${r.item.sku} ${r.item.name} — ${STATE_LABEL[r.state]}; ${r.item.critical?.why ?? ""}`)
      .join("\n");
  }
  if (/reorder|replenish|buy|order more|below/.test(text)) {
    if (!d.run.length) return t("Nothing is below its reorder point.");
    return d.run
      .map(
        (g) =>
          `${g.supplier}: ${g.lines
            .map((l) => {
              const late = arrivesInTime(l);
              return `${l.qty} × ${l.item.name}${late.inTime === false ? t(" — {n} days too late at this lead time", { n: late.shortBy ?? 0 }) : ""}`;
            })
            .join("; ")}`,
      )
      .join("\n");
  }
  if (/waiting|blocked|held up|stopped|job/.test(text)) {
    if (!d.waiting.length) return t("No job is short of a part.");
    return d.waiting.map((w) => `${w.workOrder} — ${w.short} short, ${w.sentence}`).join("\n");
  }
  if (/late|overdue|supplier|delivery|deliver/.test(text)) {
    if (!d.overdue.length) return t("No purchase order is past the date the supplier promised.");
    return d.overdue
      .map((o) =>
        t("{number} from {supplier} — {days} late, {lines} outstanding", {
          number: o.number,
          supplier: o.supplier,
          days: o.daysLate === null ? t("no date given") : tn(o.daysLate, "{n} day", "{n} days"),
          lines: tn(o.lines, "{n} line", "{n} lines"),
        }),
      )
      .join("\n");
  }
  if (/obsolete|not moved|no movement|dead stock/.test(text)) {
    const dead = d.rows.filter((r) => r.obsolete);
    return dead.length
      ? dead.map((r) => `${r.item.sku} ${r.item.name} — ${r.onHand} ${r.item.uom}, nothing drawn in a year`).join("\n")
      : t("Every line with stock has moved within the year.");
  }
  if (/value|worth|how much stock/.test(text)) {
    return d.summary.value === null
      ? t("No line carries a unit cost, so the shelf cannot be valued.")
      : t("{value} on the shelf, at the last cost recorded for each line. {unknown} lines carry no cost.", {
          value: Math.round(d.summary.value).toLocaleString(),
          unknown: d.summary.valueUnknown,
        });
  }
  if (/supplier|who supplies|contract/.test(text)) {
    const none = d.rows.filter((r) => (r.item.supplier ?? NO_SUPPLIER) === NO_SUPPLIER);
    return none.length
      ? t("These lines have no supplier recorded: {list}", { list: none.map((r) => r.item.sku).join(", ") })
      : t("Every line has a supplier recorded against it.");
  }
  return null;
}

/** Everything on order for one line, as the item page states it. */
export function incomingFor(sku: string, register: StoresRegister): { number: string; supplier: string; qty: number; expectedAt?: string }[] {
  const out: { number: string; supplier: string; qty: number; expectedAt?: string }[] = [];
  for (const o of register.orders) {
    if (o.status === "cancelled" || o.status === "received") continue;
    for (const l of o.lines) {
      if (l.sku !== sku || l.received >= l.qty) continue;
      out.push({ number: o.number, supplier: o.supplier, qty: l.qty - l.received, expectedAt: o.expectedAt });
    }
  }
  return out.sort((a, b) => (a.expectedAt ?? "9999").localeCompare(b.expectedAt ?? "9999"));
}

/** A guard so the tile and the answer cannot disagree about what is on order. */
export function totalOnOrder(sku: string, register: StoresRegister): number {
  return onOrder(sku, register);
}
